import React, { useRef, useState } from "react";

import MenuIcon from "@mui/icons-material/Menu";
import TuneIcon from "@mui/icons-material/Tune";
import { IconButton, Popover, Stack } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Menu } from "components/popups";
import Highcharts from "highcharts";
import { HighchartsReactRefObject } from "highcharts-react-official";
import exportData from "highcharts/modules/export-data";
import exporting from "highcharts/modules/exporting";
import fullscreen from "highcharts/modules/full-screen";
import offlineExporting from "highcharts/modules/offline-exporting";
import { useReportsActions, useReportsQueriesSelector } from "store";
import { Chart as ChartData, ChartSettings, DateRangeValue, ReportFilters } from "store/types";

import { ChartMenu } from "./ChartMenu";
import { EmptyCardChart } from "./EmptyCardChart";
import { MemoizedHighchart } from "./MemoizedHighchart";
import { useChartHook } from "./useChartHook";

import "./Chart.styles.scss";

offlineExporting(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

const isChartEmpty = (chartData?: ChartData["data"]["data"]) => {
  if (!chartData) return true;

  if (!Array.isArray(chartData)) return false;

  // pie / bar / column etc: data: []
  if (chartData.length === 0) return true;

  // waterfall etc: data: [{ data: [] }]
  if (chartData.length === 1 && Array.isArray((chartData[0] as any)?.data) && !(chartData[0] as any).data.length) {
    return true;
  }

  return false;
};

export interface ChartProps {
  name: string;
  chartSettings: ChartSettings;
  investorsDashboard?: boolean; // Damn prop for specific quick feature
  filters: ReportFilters;
  pageType: "chartSnapshot" | "chartInvestorView";
  internalDateRange?: DateRangeValue<string>;
  data?: ChartData;
  isLoading: boolean;
}

export const Chart: React.FC<ChartProps> = (props) => {
  const { name, chartSettings, investorsDashboard, data: externalData, isLoading } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { setQueriesAction } = useReportsActions();
  const { filters } = useReportsQueriesSelector();
  const chartRef = useRef<HighchartsReactRefObject>(null); // should be fixed

  const handleGraphClick = (filter: string, value: any) => {
    const effectiveFilter = filter === "deal" ? "deal_name" : filter;
    setQueriesAction({ filters: { ...filters, [effectiveFilter]: value } });
  };

  const chartOptions = useChartHook({
    chartData: externalData,
    setFieldValue: handleGraphClick,
    groupBy: chartSettings.groupBy,
    chart: {
      spacing: [16, 16, 10, 16]
    },
    // This is for specific quick feature investors dashboard
    ...(investorsDashboard && {
      chart: {
        height: 228,
        width: 400,
        spacing: [16, 16, 16, 16]
      },
      ...(externalData?.data.type === "pie" && {
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: {
              enabled: true,
              connectorWidth: 1,
              distance: 1,
              format: "{point.percentage:.1f}%"
            },
            showInLegend: true
          }
        },
        legend: {
          verticalAlign: "middle",
          layout: "vertical",
          align: "right",
          maxHeight: 150,
          width: 130,
          itemStyle: {
            fontSize: "14px",
            fontWeight: "normal",

            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis"
          },
          x: 0,
          y: 0
        }
      })
    })
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleExport = (type: string) => {
    (chartRef.current?.chart as any)?.exportChartLocal({ type });
  };

  const isEmpty = isChartEmpty(externalData?.data?.data);

  return (
    <figure
      className="pie-chart"
      style={{
        ...(investorsDashboard && { height: "228px" }),
        ...(investorsDashboard && { width: "400px", maxWidth: "400px" })
      }}
    >
      <AsyncContentMui isLoading={isLoading}>
        {isEmpty ? (
          <EmptyCardChart title={externalData?.title} />
        ) : (
          <MemoizedHighchart options={chartOptions} chartRef={chartRef} />
        )}
        <Stack
          className="menu-trigger-blocks"
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            flexDirection: "row",
            gap: 1
          }}
        >
          <IconButton
            className="chart-option"
            sx={{
              opacity: anchorEl ? 1 : 0,
              transition: "opacity 0.3s linear",
              zIndex: 1000
            }}
            id={"pie-chart-" + name}
            onClick={handleOpenMenu}
          >
            <TuneIcon />
          </IconButton>
          {!isEmpty && (
            <Menu
              iconButtonProps={{
                className: "chart-option",
                sx: { opacity: 0, transition: "opacity 0.3s linear", zIndex: 1000 }
              }}
              keepTriggerEnabled
              icon={<MenuIcon />}
              options={[
                {
                  title: "View in full screen",
                  onClick: () => (chartRef.current?.chart as any).fullscreen.toggle()
                },
                {
                  title: "Print chart",
                  onClick: () => (chartRef.current?.chart as any).print(),
                  hasLine: true
                },
                {
                  title: "Download PNG image",
                  onClick: () => handleExport("image/png")
                },
                {
                  title: "Download JPEG image",
                  onClick: () => handleExport("image/jpeg")
                },
                {
                  title: "Download PDF Document",
                  onClick: () => handleExport("application/pdf")
                },
                {
                  title: "Download SVG vector image",
                  onClick: () => handleExport("image/svg+xml"),
                  hasLine: true
                },
                {
                  title: "Download CSV",
                  onClick: () => (chartRef.current?.chart as any).downloadCSV()
                },
                {
                  title: "Download XLS",
                  onClick: () => (chartRef.current?.chart as any).downloadXLS()
                }
              ]}
            />
          )}
        </Stack>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          <Stack sx={{ p: "15px", boxShadow: "sm", minWidth: "350px", gap: "10px", borderRadius: "8px" }}>
            <ChartMenu
              name={name}
              chartProps={chartSettings}
              onClose={handleCloseMenu}
              isInvestorView={investorsDashboard}
            />
          </Stack>
        </Popover>
      </AsyncContentMui>
    </figure>
  );
};
